<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>Member</template>
            <template v-slot:pageDetail>List</template>
        </o-bread-crumb>

        <div v-if="thisComponentShow" class="card text-center bg-white shadow-none">
            <div class="card-header">
                <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                    <li class="nav-item">
                        <div class="d-flex d-inline-block">
                            <h4 class="header-title">
                                <i class="fas fa-users me-2 primary-icon"></i>List of Members
                            </h4>
                        </div>
                        <p class="header-subtitle me-4 mt-1">Manage registered members</p>
                    </li>
                    <li class="nav-item d-flex align-items-center gap-2">
                        <transition :name="registerOption === 0 ? 'slide-left' : 'slide-right'">
                            <button v-if="registerOption === 0" @click="showModalPendingMembers" class="btn btn-action btn-pending"> 
                                <i class="fas fa-clock me-1"></i> Pending Members
                            </button>
                        </transition>
                        <o-toggle-option v-if="readRegisterOption || writeRegisterOption" :option1="staticRegister" :option2="dynamicRegister" :selectedOption="registerOption" @option-selected="updateRegisterOption"></o-toggle-option>
                    </li>
                </ul>
            </div>
            <div class="card-body">
                <div class="spinner d-flex justify-content-center align-items-center" v-if='page.isLoading'>
                    <div class="spinner-border text-primary" role="status"></div>
                </div>

                <div class="" v-if="page.output">
                    <div class="filter-action-wrapper mb-4 mt-3">
                        <div class="filter-controls">
                            <!-- Search Control -->
                            <div class="search-box-custom">
                                <div class="search-type-tabs">
                                    <button 
                                        v-for="opt in searchOptions" 
                                        :key="opt.value"
                                        class="tab-btn" 
                                        :class="{ active: searchSelected === opt.value }"
                                        @click="searchSelected = opt.value"
                                    >
                                        {{ opt.text }}
                                    </button>
                                </div>
                                <div class="search-input-wrapper" v-show="searchSelected && !['created_at', 'member_register_tracker', 'tier'].includes(searchSelected)">
                                    <input 
                                        type="text" 
                                        class="form-control-search" 
                                        v-model="searchCriteria" 
                                        placeholder="Search..." 
                                        @keyup.enter="doSearch()" 
                                    />
                                    <button v-if="!isSearchActive" class="btn-search" @click="doSearch()">
                                        <i class="fas fa-search"></i>
                                    </button>
                                    <button v-else class="btn-search" @click="clearSearch()">
                                        <i class="fas fa-times text-danger"></i>
                                    </button>
                                </div>
                                <!-- Date Range for created_at -->
                                <div class="search-input-wrapper" v-show="searchSelected === 'created_at'">
                                    <div class="input-container-search">
                                         <input 
                                             placeholder="Select Date Range"
                                             ref="datePicker"
                                         />
                                         <i class="fas fa-calendar-alt calendar-icon"></i>
                                         <Transition name="fade">
                                            <div v-if="showDateTip" class="date-selection-tip">
                                                <i class="fas fa-info-circle"></i>
                                                Please select another date to complete the range
                                            </div>
                                        </Transition>
                                    </div>
                                    <button v-if="!isSearchActive" class="btn-search" @click="doSearch()" :disabled="!isDateRangeValid">
                                        <i class="fas fa-search"></i>
                                    </button>
                                    <button v-else class="btn-search" @click="clearSearch()">
                                        <i class="fas fa-times text-danger"></i>
                                    </button>
                                </div>
                                <!-- Tier Filter -->
                                <div class="search-input-wrapper" v-show="searchSelected === 'tier'">
                                    <div class="input-container-search">
                                        <select class="form-select custom-select-transparent" v-model="searchCriteria" @change="doSearch()">
                                            <option value="">Select Tier</option>
                                            <option v-for="t in tierList" :key="t.value" :value="t.text">{{ t.text.length > 25 ? t.text.substring(0, 25) + '..' : t.text }}</option>
                                        </select>
                                    </div>
                                    <button v-if="isSearchActive" class="btn-search" @click="clearSearch()">
                                        <i class="fas fa-times text-danger"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="action-section">
                            <div class="action-buttons-group">
                                <button class="btn btn-refresh" @click.prevent="resetState()" title="Refresh">
                                    <i class="fas fa-sync-alt"></i>
                                </button>
                                <router-link :to="{ name: 'MemberTracking' }" class="btn btn-action-secondary" title="Tracking Member">
                                    <i class="fas fa-chart-line me-1"></i> Tracking Member
                                </router-link>
                                <button v-if="write" class="btn btn-action-secondary" id="importBut" @click="showModal" title="Import">
                                    <i class="fas fa-file-import me-1"></i> Import
                                </button>
                                <button v-if="readExportMember" class="btn btn-action-secondary" @click.prevent="download_state()" title="Export All Members">
                                    <i class="fas fa-file-export me-1"></i> Export
                                </button>
                                <router-link v-if="write" :to="{ name: 'MemberForm' }" class="btn btn-add">
                                    <i class="fas fa-plus me-1"></i> Add New
                                </router-link>
                            </div>
                        </div>
                    </div>

                    <o-table style="text-align:left;" class="table-hover member-list-table">
                        <thead>
                            <tr>
                                <td class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index">
                                    {{ field }}
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in members" :key="index" class="member-row-clickable">
                                <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                                <td>{{ item.alias_id }}</td>
                                <td>{{ item.title }} {{ item.first_name }} {{ item.last_name }}</td>
                                <td>{{ item.user?.email }}</td>
                                <td>
                                    <span v-if="!item.member_tiering">Member</span>
                                    <span v-else-if="!item.member_tiering.tier" class="status-badge status-deleted">Tier has been deleted</span>
                                    <span v-else :title="item.member_tiering.tier.name">{{ item.member_tiering.tier.name.length > 10 ? item.member_tiering.tier.name.substring(0, 10) + '..' : item.member_tiering.tier.name }}</span>
                                </td>
                                <td>
                                    <span class="date-text">{{ formatDate(item.created_at) }}</span>
                                </td>
                                <td class="ml-3">
                                    <div class="d-flex gap-2" @click.stop>
                                        <router-link class="btn btn-action btn-history" :to="{ name: 'MemberHistory', params: { id: item.id } }">
                                            <i class="fas fa-history me-1"></i> History
                                        </router-link>
                                        <button class="btn btn-action btn-info-action" @click="showMemberInfo(item)">
                                            <i class="fas fa-eye me-1"></i> Info
                                        </button>
                                        <router-link v-if="write" class="btn btn-action btn-edit" :to="{ name: 'MemberEdit', params: { id: item.id }, query: { page: meta.page } }">
                                            <i class="fas fa-edit me-1"></i> Edit
                                        </router-link>
                                        <div v-if="write">
                                            <a v-if="item.transaction.length == 0" href="#" 
                                                class="btn btn-action btn-delete"
                                                @click.prevent="destroy(item.id, index, `${item.first_name} ${item.last_name}`)">
                                                <i class="fas fa-trash-alt me-1"></i> Delete
                                            </a>
                                            <a v-else href="#" 
                                                class="btn btn-action btn-delete"
                                                @click.prevent="showPendingTransactionWarning()">
                                                <i class="fas fa-trash-alt me-1"></i> Delete
                                            </a>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="members.length === 0">
                                <td colspan="7" class="text-center">No member registered</td>
                            </tr>
                        </tbody>
                    </o-table>
                </div>
            </div>

            <div class="container mt-5">
                <o-pagination :page="meta.page" :total="meta.total" @callback="navPage"/>
            </div>
        </div>

        <MemberInfoModal 
            :isVisible="showInfoModal"
            :member="selectedMember"
            @close="showInfoModal = false"
        />

        <import-member
            v-if="isModalVisible"
            @saveDetail="handleImport" 
            @closeModal="closeModal"
            @downloadTemplate="downloadTemplate"
        ></import-member>

        <o-pending-member
            v-if="isModalPendingMembersVisible"
            :isVisible="isModalPendingMembersVisible"
            :writeRegisterOption="writeRegisterOption"
            @close="closeModalPendingMember"
        ></o-pending-member>

        <!-- Warning box for members with pending transactions -->
        <o-warning-box
            v-if="isPendingTransactionWarningVisible"
            title="Cannot Delete Member"
            message="This member cannot be deleted because there is a pending transaction. Please resolve all pending transactions before attempting to delete this member."
            action-text="Understood"
            cancel-text="Close"
            @action="closePendingTransactionWarning"
            @cancel="closePendingTransactionWarning"
            :hide-action="true"
        />

        <o-warning-box
            v-if="isDeleteConfirmationVisible"
            title="Confirm Delete"
            :item-name="memberToDelete?.name || 'this member'"
            warning-message="This action cannot be undone."
            confirm-text="Delete"
            :is-loading="page.isLoading"
            @confirm="confirmDelete"
            @cancel="cancelDelete"
            :hide-action="false"
        />

        <ImportResultModal 
            :isVisible="importMessageStatus"
            :data="importMessage.count"
            @close="importMessageStatus = false"
        />
    </div>
</template>

<script>

import store from '@/store/'
import {mapState} from 'vuex'
import {OTable, OPagination, OBreadCrumb, OToggleOption, OPendingMember, OWarningBox} from '@/components/OmnihControl'
import { computed, onMounted, reactive, ref, watch, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ImportMember from './member/ImportMember.vue';
import MemberInfoModal from '@/components/member/MemberInfoModal.vue';
import ImportResultModal from '@/components/member/ImportResultModal.vue';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { useToast } from '@/composables/useToast';

export default {
    setup() {
        const route = useRoute();
        const router = useRouter();
        const { showToast } = useToast();
        let lastActivePage = 1;

        let meta = computed(() => store.state.Member.meta)

        let page = reactive({
            isLoading: false,
            output: false,
            component: 'member',
            component2: 'register-option'
        });

        let isModalPendingMembersVisible = ref(false);

        const memberSearch = ref([]);

        let isModalVisible = ref(false);
        let uploadedFile = ref(null);
        const validation = ref('');
        
        // For pending transaction warning
        const isPendingTransactionWarningVisible = ref(false);
        const isDeleteConfirmationVisible = ref(false);
        const memberToDelete = ref({ id: null, index: null, name: '' });
        const showDateTip = ref(false);

        // For member info modal
        const showInfoModal = ref(false);
        const selectedMember = ref({});

        const registerOption = ref();
        const staticRegister = ref('Static Register');
        const dynamicRegister = ref('Dynamic Register');

        const selectMember = ref();

        // New inline search state
        const searchSelected = ref('alias_id');
        const searchCriteria = ref('');
        const searchDateStart = ref('');
        const searchDateEnd = ref('');
        const searchCampaignId = ref('');
        const datePicker = ref(null);
        let flatpickrInstance = null;
        const isSearchActive = ref(false);

        const isDateRangeValid = computed(() => searchDateStart.value && searchDateEnd.value);

        const searchOptions = reactive([
            { value: 'alias_id', text: 'Member ID' },
            { value: 'name', text: 'Name' },
            { value: 'email', text: 'Email' },
            { value: 'created_at', text: 'Date' },
            { value: 'tier', text: 'Tier' }
        ])

        let fields = ref([
            'No',
            'Member ID',
            'Name',
            'Email',
            'Tier',
            'Registered Date',
            'Action'
        ]);

        const importMessageStatus = ref(false);

        const importMessage = ref({
            count: {
                fail_count: 0,
                success_count: 0,
                duplicate_emails: []
            }
        });
        const uniqueDuplicateEmails = computed(() => {
            return [...new Set(importMessage.value.count.duplicate_emails.map(email => email.replace(/["[\]]/g, '')))];
        });

        const thisComponentShow = ref(true);

        const selfLoaded = ref(store.state.Auth.selfLoaded);

        watch(
            () => store.state.Auth.selfLoaded,
            (newVal) => {
                if (newVal) {
                    selfLoaded.value = newVal;
                    load_state();
                }
            }
        );

        watch(datePicker, (newVal) => {
            if (newVal && !flatpickrInstance) {
                flatpickrInstance = flatpickr(newVal, {
                    mode: 'range',
                    onChange: (selectedDates) => {
                        if (selectedDates.length === 2) {
                            searchDateStart.value = selectedDates[0].toISOString().split('T')[0];
                            searchDateEnd.value = selectedDates[1].toISOString().split('T')[0];
                        } else {
                            searchDateStart.value = '';
                            searchDateEnd.value = '';
                        }
                    },
                    onValueUpdate: (selectedDates) => {
                        showDateTip.value = selectedDates.length === 1;
                    },
                    onClose: () => {
                        showDateTip.value = false;
                    }
                });
            }
        });

        onMounted(() => {
            if (selfLoaded.value) {
                // If there's a page query param, set it before loading
                if (route.query.page) {
                    const newMeta = { ...meta.value, page: parseInt(route.query.page) };
                    store.commit('Member/switch_page', { meta: newMeta });
                } else if (!route.query.newMember) {
                    store.commit('Member/reset_meta');
                }
                load_state();
            }
        });

        onBeforeUnmount(() => {
            const updatedMeta = { ...meta.value, page: lastActivePage };
            store.commit('Member/switch_page', { meta: updatedMeta });

            store.commit('Member/clear_search');
            store.dispatch('Member/read').then(() => {});
        });

        const write = ref(false);

        const readRegisterOption = ref(true);
        const writeRegisterOption = ref(false);

        const writeExportMember = ref(false);
        const readExportMember = ref(false);

        function showModal() {
            isModalVisible.value = true; 
            uploadedFile.value = null;
        }

        function closeModal() {
            isModalVisible.value = false;
        }

        function handleImport(formData) {
            validation.value = '';
            store.dispatch('Auth/getBusinessProfile')
                .then(businessProfile => {
                    const fileData = Array.from(formData.entries()).find(([key]) => key === 'file');
                    const fileName = fileData[1].name;
                    formData.append('file', fileName);                
                    formData.append('corporate_id', businessProfile.corporate_id);
                    page.isLoading = true;
                    store.dispatch('Member/import', { data: formData } )
                        .then((response) => {
                            importMessageStatus.value = true;
                            importMessage.value.count.fail_count = response.data.message.fail_count;
                            importMessage.value.count.success_count = response.data.message.success_count;
                            importMessage.value.count.duplicate_emails = [...new Set((response.data.message.duplicate_emails || []).map(email => email.replace(/["[\]]/g, '')))];
                            importMessage.value.count.validation_errors = response.data.message.validation_errors || [];
                            load_state();
                        })
                        .catch((error) => {
                            console.error('Import failed:', error);
                            if (error.response && error.response.data && error.response.data.error) {
                                const errorMessage = error.response.data.error;

                                if (Object.keys(errorMessage).length === 0) {
                                    validation.value = "Don't match for file xls or csv members";
                                } else if (errorMessage.includes("PDOException: SQLSTATE[23000]: Integrity constraint violation: 1062 Duplicate entry")) {
                                    const emailRegex = /'([^']+)' for key 'users_email_unique'/;
                                    const match = errorMessage.match(emailRegex);
                                    
                                    if (match && match[1]) {
                                        const duplicateEmail = match[1];
                                        validation.value = `Member can't be saved because the email ${duplicateEmail} is already in use.`;
                                    } else {
                                        validation.value = "Member can't be saved due to a duplicate entry.";
                                    }
                                } else if (errorMessage.includes('null')) {
                                    validation.value = `Error on row with null value: ${errorMessage}`;
                                } else {
                                    validation.value = errorMessage;
                                }

                                console.log('Validation error:', validation.value);
                            }else if(error.response && error.response.status === 403){
                                validation.value = error.response.data.message;
                            } else {
                                validation.value = "Don't match for file xls or csv merchants";
                            }
                            
                            showToast({
                                type: 'error',
                                title: 'Import Failed',
                                message: validation.value
                            });
                            
                            page.isLoading = false;
                            throw error.response.data.error;
                        });
                })
                .catch(error => {
                    console.error('Failed to fetch business profile:', error);
                });
            page.isLoading = false;
        }

        function onFileChange(event) {
            uploadedFile.value = event.target.files[0]; 
        }

        function download_state() {
            page.isLoading = true;

            store.dispatch('Member/export')
            .then((response) => {
                handleDownloadResponse(response);
                })
            .catch((err) =>{
                validation.value = err.response.data.message;
            })
            .finally(()=>{
                page.isLoading = false;
                page.output=true;
            });
        }

        function handleDownloadResponse(response) {
            try {
                const contentDispositionHeader = response.headers?.['content-disposition'];
                const fallbackName = 'members_export_' + new Date().toISOString().slice(0,19).replace(/[:T]/g, '-') + '.csv';

                const fileName = contentDispositionHeader 
                    ? contentDispositionHeader.split('filename=')[1]?.replace(/"/g, '') || fallbackName
                    : fallbackName;

                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Download error:', error);
                alert('Export failed. Please try again or contact support.');
            }
        }

        function readRegisterOptionApi(filter) {
            store.dispatch('RegisterOption/read_single', filter.corporate_id)
            .then((resp) => {
                registerOption.value = resp.data.data;
            })
            .catch((err) => {
                console.error(err);
                readRegisterOption.value = false;
            });
        }

        function load_state() {
            page.isLoading = true;
            validation.value = '';

            store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                
                const filter = {
                    corporate_id: businessProfile.corporate_id
                }

                store.commit('Member/add_filter', {filter});
                
                store.dispatch('Member/read')
                .then(() => {
                    if (route.query.newMember === 'true') {
                        const lastPage = meta.value.total;
                        router.replace({ name: 'Member', query: { page: lastPage } });
                    } else {
                        page.isLoading = false;
                        page.output = true;
                    }
                })
                .catch((err) => {
                    validation.value = err.response?.data?.message || err.message;
                    console.error(err);
                    thisComponentShow.value = false;
                    page.isLoading = false;
                });
                if (store.state.Auth.currUser.level === 'root') {
                    write.value = true;
                    writeRegisterOption.value = true;
                    writeExportMember.value = true;
                    readExportMember.value = true;
                } else {
                    const userModules = store.state.Auth.userInfo.modules;

                    if (userModules) {
                        const matchingModule = userModules.find(module => module.module_name === page.component);

                        const exportMemberModules = userModules.find(module => module.module_name === 'export-member');
                        readExportMember.value = exportMemberModules ? exportMemberModules.read || exportMemberModules.write : false;
                        writeExportMember.value = exportMemberModules ? exportMemberModules.write : false;
                        
                        write.value = matchingModule ? matchingModule.write : false;

                        const matchingModule2 = userModules.find(module => module.module_name === page.component2);
                        writeRegisterOption.value = matchingModule2 ? matchingModule2.write : false;
                    } else {
                        console.error('userModules is undefined');
                        write.value = false;
                    }
                }
                readRegisterOptionApi(filter);
                store.dispatch('MemberTier/read_all', businessProfile.corporate_id);
            });
        }

        function showModalPendingMembers() {
            load_state();
            isModalPendingMembersVisible.value = true;
        }

        function closeModalPendingMember() {
            load_state();
            isModalPendingMembersVisible.value = false;
        }

        function destroy(id, index, name) {
            memberToDelete.value = { id, index, name };
            isDeleteConfirmationVisible.value = true;
        }

        function confirmDelete() {
            page.isLoading = true;
            
            store.dispatch('Member/delete', memberToDelete.value.id)
            .then(() => {
                showToast({
                    type: 'success',
                    title: 'Deleted',
                    message: `Member has been deleted successfully.`
                });
                load_state();
            }).catch((err) => {
                console.log(err);
                validation.value = err.message;
            }).finally(() => {
                page.isLoading = false;
                isDeleteConfirmationVisible.value = false;
                memberToDelete.value = { id: null, index: null, name: '' };
            });
        }

        function cancelDelete() {
            isDeleteConfirmationVisible.value = false;
            memberToDelete.value = { id: null, index: null, name: '' };
        }

        // New search methods (replacing OSearch)
        function doSearch() {
            const selected = searchSelected.value;
            const criteria = searchCriteria.value;
            const date_start = searchDateStart.value;
            const date_end = searchDateEnd.value;

            if (!selected) return;

            let search = {};
            if (selected === 'created_at' && date_start && date_end) {
                search[selected] = `${date_start}, ${date_end}`;
            } else if (selected === 'tier' && criteria) {
                search['tier'] = criteria;
            } else if (criteria && selected !== 'created_at' && selected !== 'tier') {
                search[selected] = criteria;
            } else {
                return;
            }

            isSearchActive.value = true;

            if (meta.value.page !== 1) {
                lastActivePage = meta.value.page;
                const updatedMeta = { ...meta.value, page: 1 };
                store.commit('Member/switch_page', { meta: updatedMeta });
                store.commit('Member/set_search', { search });
                store.dispatch('Member/read').then(() => {});
            } else {
                store.commit('Member/set_search', { search });
                const updatedMeta = { ...meta.value, page: 1 };
                store.commit('Member/switch_page', { meta: updatedMeta });
                store.dispatch('Member/read');
            }
        }

        function clearSearch() {
            isSearchActive.value = false;
            searchCriteria.value = '';
            searchDateStart.value = '';
            searchDateEnd.value = '';
            searchCampaignId.value = '';

            if (flatpickrInstance) {
                flatpickrInstance.clear();
            }

            const updatedMeta = { ...meta.value, page: lastActivePage };
            store.commit('Member/switch_page', { meta: updatedMeta });

            store.commit('Member/clear_search');
            store.dispatch('Member/read').then(() => {});
        }

        function resetState() {
            searchSelected.value = 'alias_id';
            searchCriteria.value = '';
            searchDateStart.value = '';
            searchDateEnd.value = '';
            searchCampaignId.value = '';
            isSearchActive.value = false;

            if (flatpickrInstance) {
                flatpickrInstance.clear();
            }

            store.commit('Member/clear_search');
            const updatedMeta = { ...meta.value, page: 1 };
            store.commit('Member/switch_page', { meta: updatedMeta });
            load_state();
        }

        function navPage(page){
            const _meta = {... meta.value, page}

            store.commit('Member/switch_page',{meta:_meta})
            load_state()
        }
        
        function updateRegisterOption(newOption) {
            page.isLoading = true;

            store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                const data = {
                    id: businessProfile.corporate_id,
                    status: newOption
                };
                
                store.dispatch('RegisterOption/update_register_option', { data })
                .then(() => {
                    console.log('Register option updated:', newOption);
                    load_state();
                    page.isLoading = false;
                })
                .catch(error => {
                    console.error('Error updating register option:', error);
                    page.isLoading = false;
                    validation.value = error.response.data.message;
                });
            });            
        }

        function downloadTemplate() {
            page.isLoading = true;
            try {
                const column = [
                    ['title', 'first_name', 'last_name', 'email', 'address', 'state', 'country', 'date_of_birth', 'preference', 'phone', 'password'],
                    ['--- PLEASE DELETE THE EXAMPLE ROW BELOW BEFORE IMPORTING ---', '', '', '', '', '', '', '', '', '', ''],
                    ['Mr.', 'John', 'Doe Example', 'johndoe@example.com', 'Example Jl. Sudirman No 1', 'Bali', 'Indonesia', '1990-01-01', 'Like a Coffe', '81234567890', 'Pass123!']
                ];
                const workbook = XLSX.utils.book_new();
                const worksheet = XLSX.utils.aoa_to_sheet(column);
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

                const xlsxContent = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                const blob = new Blob([xlsxContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;' });

                saveAs(blob, 'MemberImportTemp.xlsx');
            } catch (error) {
                console.error('Error downloading template:', error); 
            } finally {
                page.isLoading = false;
            }
        }

        function formatDate(dateString) {
            const date = new Date(dateString);
            const options = { year: 'numeric', month: 'short', day: '2-digit' };
            const formattedDate = date.toLocaleDateString('en-US', options);

            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');

            return `${formattedDate}, ${hours}:${minutes}:${seconds}`;
        }

        function showPendingTransactionWarning() {
            isPendingTransactionWarningVisible.value = true;
        }
        
        function closePendingTransactionWarning() {
            isPendingTransactionWarningVisible.value = false;
        }

        async function showMemberInfo(item) {
            try {
                page.isLoading = true;
                const memberData = await store.dispatch('Member/read_single', item.id);
                
                selectedMember.value = {
                    id: memberData.id,
                    alias_id: memberData.alias_id,
                    title: memberData.title,
                    first_name: memberData.first_name,
                    last_name: memberData.last_name,
                    email: memberData.user?.email,
                    phone: memberData.user?.user_profile?.phone,
                    date_of_birth: memberData.user?.user_profile?.date_of_birth,
                    preference: memberData.user?.user_profile?.preference,
                    avatar: memberData.user?.user_profile?.avatar?.image_url,
                    tier_name: memberData.member_tiering?.tier?.name,
                    points: memberData.points,
                    point_balance: memberData.point_balance,
                };
                showInfoModal.value = true;
            } catch (error) {
                console.error('Error fetching member data:', error);
                validation.value = 'Failed to load member information';
            } finally {
                page.isLoading = false;
            }
        }

        return {
            page,
            destroy,
            load_state,
            fields,
            meta,
            navPage,
            memberSearch,
            selectMember,
            searchOptions,
            searchSelected,
            searchCriteria,
            searchDateStart,
            searchDateEnd,
            searchCampaignId,
            isSearchActive,
            doSearch,
            clearSearch,
            resetState,
            staticRegister,
            dynamicRegister,
            registerOption,
            updateRegisterOption,
            showModalPendingMembers,
            isModalPendingMembersVisible,
            showDateTip,
            closeModalPendingMember,
            isModalVisible,
            showModal,
            closeModal,
            handleImport,
            onFileChange,
            validation,
            downloadTemplate,
            importMessage,
            importMessageStatus,
            uniqueDuplicateEmails,
            formatDate,
            thisComponentShow,
            selfLoaded,
            write,
            writeRegisterOption,
            download_state,
            handleDownloadResponse,
            writeExportMember,
            readExportMember,
            datePicker,
            isDateRangeValid,
            readRegisterOption,
            isPendingTransactionWarningVisible,
            showPendingTransactionWarning,
            closePendingTransactionWarning,
            isDeleteConfirmationVisible,
            memberToDelete,
            confirmDelete,
            cancelDelete,
            showInfoModal,
            selectedMember,
            showMemberInfo,
            showToast
        }
    },
    computed: {
    ...mapState({
        members: state => state.Member.members,
        campaigns: state => state.Campaign.campaigns,
        allTiers: state => state.MemberTier.allTiers,
    }),
    campaignList() {
        return Array.isArray(this.campaigns)
            ? this.campaigns.map(item => ({
                value: item.id,
                text: item.name,
            }))
            : [];
        },
    tierList() {
        return Array.isArray(this.allTiers)
            ? this.allTiers.map(item => ({
                value: item.id,
                text: item.name,
            }))
            : [];
        }
    },
    components:{
        OTable,
        OPagination,
        OBreadCrumb,
        OToggleOption,
        OPendingMember,
        ImportMember,
        OWarningBox,
        MemberInfoModal,
        ImportResultModal
    }
}
</script>

<style scoped>
/* Filter and Action Bar Styling (Matched with Newsletter Pattern) */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-section, .action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-label-tabs {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.label-tab {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color, #0d6efd);
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

/* Custom Search Box */
.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: max-content;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
    flex-wrap: wrap;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color, #0d6efd);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
}

.search-input-wrapper input,
.search-input-wrapper select.custom-select-transparent {
    border: none;
    padding: 10px 16px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
    box-shadow: none;
}

.search-input-wrapper select.custom-select-transparent:focus {
    box-shadow: none;
    border: none;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color, #0d6efd);
}

.search-input-wrapper .btn-search {
    position: relative;
    width: 44px;
    height: 42px;
    border-radius: 0 8px 8px 0;
    background: transparent;
}

.search-input-wrapper .btn-search:hover {
    background: #f1f5f9;
}

.search-input-wrapper .btn-search::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1.1px;
    height: 60%;
    background: linear-gradient(to bottom, rgba(203, 213, 225, 0), rgba(203, 213, 225, 1), rgba(203, 213, 225, 0));
    pointer-events: none;
}

.btn-search:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Date Range Inputs */
.date-range-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.input-container-search {
    position: relative;
    width: 100%;
    flex: 1;
}

.input-container-search input {
    padding-right: 40px !important;
    cursor: pointer;
    width: 100%;
}

.calendar-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--primary-color, #0d6efd);
    pointer-events: none;
}

.date-separator {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
}

/* Pseudo Date Range Box for Alignment Consistency */
.date-range-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
}

.status-filter-box {
    display: flex;
    align-items: center;
}

.form-control-filter {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    height: 46px;
    min-width: 150px;
    background-color: white;
}

.form-control-filter:focus {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
    outline: none;
}

/* Action Buttons */
.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 8px;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.btn-action-secondary {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-weight: 600;
    font-size: 13px;
    padding: 0 16px;
    height: 40px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
    cursor: pointer;
}

.btn-action-secondary:hover {
    background-color: #e2e8f0;
    color: #1e293b;
}

.btn-add {
    background-color: var(--primary-color, #0d6efd);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
}

.btn-add:hover {
    filter: brightness(0.9);
    color: white;
}

/* Table Styles */
.member-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.member-list-table th,
.member-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.member-row-clickable {
    transition: background-color 0.2s;
}

.member-row-clickable:hover {
    background-color: #f8fafc !important;
}

.status-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: capitalize;
}

.status-deleted {
    background: #fff1f2;
    color: #9f1239;
}

.date-text {
    font-size: 13px;
    color: #4a5568;
    white-space: nowrap;
}

/* Action Buttons in Table */
.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
    cursor: pointer;
}

.btn-history {
    background-color: #ecfdf5;
    color: #065f46;
}

.btn-history:hover {
    background-color: #d1fae5;
    color: #064e3b;
}

.btn-info-action {
    background-color: #eff6ff;
    color: #1e40af;
}

.btn-info-action:hover {
    background-color: #dbeafe;
    color: #1e3a8a;
}

.btn-edit {
    background-color: #f3f4f6;
    color: #374151;
}

.btn-edit:hover {
    background-color: #e5e7eb;
    color: #111827;
}

.btn-delete {
    background-color: #fff1f2;
    color: #9f1239;
}

.btn-delete:hover {
    background-color: #ffe4e6;
    color: #be123c;
}

.btn-pending {
    background-color: #fffbeb;
    color: #92400e;
    font-size: 13px;
    padding: 8px 16px;
}

.btn-pending:hover {
    background-color: #fef3c7;
    color: #78350f;
}

/* Summary Cards */
.total-summary {
    padding: 0 10px;
}

.summary-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    height: 100%;
}

.summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.summary-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background-color: var(--primary-color);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    flex-shrink: 0;
}

.summary-details {
    flex: 1;
    min-width: 0;
}

.summary-label {
    font-size: 11px;
    font-weight: 600;
    color: #6c757d;
    margin: 0 0 2px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.summary-value {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    color: #212529;
}

/* Header */
.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    text-align: left;
}

/* Import Message Box */
.message-box {
    border: 1px solid #ccc;
    border-radius: 12px;
    padding: 20px;
    background: linear-gradient(135deg, #cb5b5b 0%, #fc6666 100%);
    color: #fff;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    margin-top: 20px;
    margin-bottom: 16px;
    animation: fadeIn 0.5s ease-in-out;
}

.message-box p {
    margin: 0;
    padding: 8px 0;
}

.text-failed {
    color: red;
    font-weight: bold;
    text-shadow: 1px 1px 4px #ffffff;
    animation: blink 1s infinite;
}

.text-duplicated {
    color: white;
}

.text-succeeded {
    color: white;
}

@keyframes blink {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsive */
@media (max-width: 991px) {
    /* Header Responsive */
    .card-header-pills {
        flex-direction: column;
        align-items: flex-start !important;
        gap: 16px;
        margin-right: 0 !important;
    }

    .card-header-pills .nav-item {
        width: 100%;
    }

    .card-header-pills .nav-item.d-flex.align-items-center {
        flex-direction: column;
        align-items: stretch !important;
        gap: 12px !important;
    }

    .btn-pending {
        width: 100%;
        justify-content: center;
    }

    .filter-action-wrapper {
        flex-direction: column;
        align-items: stretch;
        gap: 20px;
    }

    .filter-controls {
        width: 100%;
    }
    
    .search-box-custom {
        width: 100%;
    }

    .search-type-tabs {
        width: 100%;
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        padding: 6px;
        background: #f8fafc;
        scrollbar-width: none;
    }

    .search-type-tabs::-webkit-scrollbar {
        display: none;
    }

    .tab-btn {
        flex: 0 0 auto;
        white-space: nowrap;
    }

    .action-buttons-group {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
    
    .btn-refresh {
        width: 100%;
    }

    .btn-action-secondary {
        width: 100%;
        justify-content: center;
        padding: 0 12px;
    }

    .btn-add {
        grid-column: span 2;
        width: 100%;
        justify-content: center;
        margin-top: 4px;
    }

    .date-range-box, .status-filter-box, .form-control-filter {
        width: 100%;
    }

    .date-range-inputs {
        flex-wrap: wrap;
    }
}

/* Spinner Overlay */
.spinner {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    z-index: 200;
}

.centered-text {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    text-align: center;
}

.no-scroll {
    overflow: hidden;
}

.mr-2 {
    margin-right: 0.5rem;
}

#importBut {
    position: relative;
}
</style>